import React from 'react';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import get from 'lodash.get';
import { connect } from 'react-redux';
import qs from 'query-string';
import produce from 'immer';

import { AlbumCustomPrintForCheckoutFromAlbum } from '../../graphql/queries/custom-prints';
import { openModal } from '../../../../actions/modal-actions';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import { checkoutPrice } from '../../../../graphql/queries/checkout';
import CustomPrintCheckout from '../../components/custom-prints/checkout/CustomPrintCheckout';
import { MeWithIdentities } from '../../graphql/queries/account';
import useCustomPrintCheckoutState, {
  CustomPrintCheckoutStateProvider,
} from '../../hooks/useCustomPrintCheckoutState';
import { accountAddressCreate } from '../../graphql/mutations/account';
import { CustomPrintConfigurationPhotos } from '../../../../graphql/queries/custom-prints';
import { createCustomPrintCandidatePhotoGroups } from '../../../../components/global/CustomPrintConfigurator/helpers';

const withAlbumCustomPrintFromAlbum = graphql(
  AlbumCustomPrintForCheckoutFromAlbum,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      loading,
      albumCustomPrint: get(album, 'custom_prints.records', [])[0],
    }),
  },
);

// Note vmasto: Again not sure about this pattern with the HOC provider
// components (this is part of notes in `useCustomPrintCheckoutState`.js).
// This pattern requires mainly two components, one provider and another to
// supply state to the rest of the container HOCs like we do in
// `withCustomPrintConfigurationPhotos` below. It seems a bit too verbose.
const withCustomPrintCheckoutStateProvider = WrappedComponent => props => {
  // eslint-disable-next-line react/prop-types
  const { location } = props;
  const params = qs.parse(location.search);

  return (
    <CustomPrintCheckoutStateProvider
      defaultState={{
        activeIdentityId: get(params, 'identity_id', ''),
      }}
    >
      <WrappedComponent {...props} />
    </CustomPrintCheckoutStateProvider>
  );
};

const withCustomPrintCheckoutState = WrappedComponent => props => {
  const {
    state: { activeIdentityId },
  } = useCustomPrintCheckoutState();

  return <WrappedComponent {...props} activeIdentityId={activeIdentityId} />;
};

const withUserAccount = graphql(MeWithIdentities, {
  props: ({ data: { me, loading } }) => ({
    account: me,
    loadingAccount: loading,
  }),
});

const withCustomPrintConfigurationPhotos = graphql(
  CustomPrintConfigurationPhotos,
  {
    options: ownProps => ({
      variables: {
        identityId: ownProps.activeIdentityId,
        albumCustomPrintId: ownProps.albumCustomPrint.id,
      },
    }),
    props: ({ data: { customPrintConfigurationPhotos, fetchMore } }) => ({
      candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
        source: customPrintConfigurationPhotos,
        mappings: [
          {
            id: 'matches',
            label: 'Matches',
            path: 'matched_photos_extended',
            mapper: record => record.photo,
          },
          {
            id: 'favorites',
            label: 'Favorites',
            path: 'favorited_photos',
          },
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'all_photos',
          },
        ],
        fetchMore,
      }),
    }),
    skip: ({ activeIdentityId, albumCustomPrint }) =>
      !activeIdentityId || !albumCustomPrint,
  },
);

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({ account, albumCustomPrint, activeIdentityId }) => ({
    variables: {
      account_id: account.id,
      custom_print_items: [
        {
          album_custom_print_id: albumCustomPrint.id,
          identity_id: activeIdentityId,
          quantity: 1,
          include_shipping: true,
        },
      ],
    },
  }),
  props: ({ data: { checkoutPrice: response } }) => ({
    price: response,
  }),
  skip: ({ account, albumCustomPrint, activeIdentityId }) =>
    !account || !albumCustomPrint || !activeIdentityId,
});

const withCheckout = graphql(checkoutMutation, {
  props: ({ mutate }) => ({
    checkout: ({
      accountId,
      accountAddressId,
      email,
      stripeTokenId,
      customPrintItems,
      stripePaymentMethodId,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            email_address: email,
            account_address_id: accountAddressId,
            stripe_token: stripeTokenId,
            custom_print_items: customPrintItems,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
          },
        },
      }),
  }),
});

const withAccountAddressCreate = graphql(accountAddressCreate, {
  props: ({ mutate }) => ({
    accountAddressCreate: (userId, addressData) =>
      mutate({
        variables: {
          input: {
            ...addressData,
            account_id: userId,
          },
        },
        update: (store, { data: { accountAddressCreate: response } }) => {
          const data = store.readQuery({
            query: MeWithIdentities,
          });

          const address = response.account_address;
          const updatedData = produce(data, draft => {
            draft.me.addresses.unshift(address);
          });

          store.writeQuery({
            query: MeWithIdentities,
            data: updatedData,
          });
        },
      }),
  }),
});

const mapDispatchToProps = {
  openModal,
};

export default compose(
  withRouter,
  withCustomPrintCheckoutStateProvider,
  withCustomPrintCheckoutState,
  withUserAccount,
  withAlbumCustomPrintFromAlbum,
  withCustomPrintConfigurationPhotos,
  withCheckoutPrice,
  withCheckout,
  withAccountAddressCreate,
  connect(null, mapDispatchToProps),
)(CustomPrintCheckout);
